/**
 * Bilingual UI — English and Hindi.
 *
 * Strings live in one flat dictionary keyed by dotted path. Externalising them
 * from the first line of code is deliberate: retrofitting localisation into
 * hard-coded JSX is expensive and always incomplete.
 *
 * Devanagari renders through the system font stack (`Noto Sans Devanagari` on
 * Android), so there is no webfont to download — which matters on a field
 * phone over 4G.
 */

import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import * as idb from './idb';

export type Locale = 'en' | 'hi';

type Dict = Record<string, string>;

const en: Dict = {
  // App
  'app.name': 'GD Strategic',
  'app.offline': 'Offline',
  'app.offlineHint': 'Your work is saved and will sync when you reconnect',
  'app.syncing': 'Syncing…',
  'app.pendingSync': '{n} waiting to sync',
  'app.syncFailed': '{n} failed to sync',
  'app.retry': 'Retry',
  'app.loading': 'Loading…',
  'app.somethingWrong': 'Something went wrong',
  'app.tryAgain': 'Try again',
  'app.noData': 'Nothing to show yet',
  'app.staleData': 'Showing saved data',
  'app.save': 'Save',
  'app.edit': 'Edit',
  'app.cancel': 'Cancel',
  'app.confirm': 'Confirm',
  'app.close': 'Close',
  'app.back': 'Back',
  'app.next': 'Next',
  'app.done': 'Done',
  'app.search': 'Search',
  'app.filter': 'Filter',
  'app.clear': 'Clear',
  'app.total': 'Total',
  'app.today': 'Today',
  'app.yesterday': 'Yesterday',
  'app.optional': 'optional',
  'app.required': 'required',
  'app.language': 'Language',
  'app.logout': 'Sign out',
  'app.account': 'Your account',
  'app.settings': 'Settings',
  'app.myDetails': 'My details',
  'app.logoutConfirm':
    'Anything still waiting to be sent will be lost. Check the sync strip first.',
  'app.call': 'Call',
  'app.directions': 'Directions',
  'app.photo': 'Photo',
  'app.takePhoto': 'Take photo',
  'app.retakePhoto': 'Retake',
  'app.remove': 'Remove',
  'app.submit': 'Submit',
  'app.submitting': 'Submitting…',
  'app.all': 'All',
  'app.view': 'View',
  'app.viewPhoto': 'View photo',
  'app.notesOptional': 'Notes (optional)',
  'app.reason': 'Reason',
  'app.from': 'From',
  'app.to': 'To',
  'app.export': 'Export CSV',
  'app.refresh': 'Refresh',
  'app.showMore': 'Show more',

  // Auth
  'auth.welcome': 'Welcome',
  'auth.welcomeLine': 'Welcome to GD Strategic — sign in to continue',
  // Shown in bold red on the first screen: the OTP arrives by WhatsApp, so the
  // user must message the business number once and keep it saved.
  'auth.otpNote': 'To receive your OTP, send "Hi" on WhatsApp to {number}. '
                + 'Please save this number as "GD Strategic Partner" in your phone.',
  'auth.enterMobile': 'Enter your mobile number',
  'auth.mobileHint': 'We will send a code on WhatsApp',
  'auth.mobile': 'Mobile number',
  'auth.sendCode': 'Send code',
  'auth.enterCode': 'Enter the 6-digit code',
  'auth.codeSentTo': 'Sent on WhatsApp to {phone}',
  'auth.verify': 'Verify',
  'auth.resend': 'Resend code',
  'auth.resendIn': 'Resend in {s}s',
  'auth.enterPin': 'Enter your PIN',
  'auth.pinHint': 'Use the 4-digit PIN you set',
  'auth.pin': 'PIN',
  'auth.signIn': 'Sign in',
  'auth.setPin': 'Set a PIN',
  'auth.setPinHint': 'You will use this instead of a code next time',
  'auth.confirmPin': 'Confirm PIN',
  'auth.pinMismatch': 'The two PINs do not match',
  'auth.useCode': 'Sign in with a code instead',
  'auth.pendingApproval': 'Your account is awaiting approval',
  'auth.pendingApprovalHint': 'We will notify you once it is approved',

  // The WhatsApp "hi" step. Wording matters here: this is the one screen that
  // asks the user to do something outside the app, and the reason has to sound
  // like a WhatsApp rule rather than our app being broken.
  'auth.hiTitle': 'Send "hi" on WhatsApp first',
  'auth.hiWhy': 'WhatsApp only allows us to send your code after you have messaged us. '
              + 'One message and the code comes straight away.',
  'auth.hiWhyAgain': 'WhatsApp needs a fresh message from you before we can send a code again.',
  'auth.hiRefused': 'WhatsApp would not deliver the code — it has not received your message yet. '
                  + 'Send "hi" and then try again.',
  'auth.hiOpen': 'Open WhatsApp and send "hi"',
  'auth.hiAlreadySent': 'I have already sent it — send my code',
  'auth.hiSendTo': 'Send to {number}',
  'auth.hiManual': 'If WhatsApp did not open, message {number} yourself and then tap the button below.',
  'auth.hiChanged': 'Use a different number',
  'auth.staySignedIn': 'You will stay signed in on this phone for 6 months.',

  // Roles
  'role.customer': 'Customer',
  'role.salesperson': 'Salesperson',
  'role.driver': 'Driver',
  'role.owner_admin': 'Admin',

  // Navigation
  'nav.home': 'Home',
  'nav.catalogue': 'Catalogue',
  'nav.cart': 'Cart',
  'nav.orders': 'Orders',
  'nav.ledger': 'Payments',
  'nav.chat': 'Chat',
  'nav.myDetails': 'My details',
  'nav.mapView': 'Map view',
  'nav.day': 'My day',
  'nav.beat': 'Beat',
  'nav.customers': 'Customers',
  'nav.reminders': 'Reminders',
  'nav.requirements': 'Requirements',
  'nav.routes': 'Routes',
  'nav.dashboard': 'Dashboard',
  'nav.approvals': 'Approvals',
  'nav.stock': 'Stock',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.more': 'More',
  // Sidebar destinations that used to exist only in the Dashboard's card.
  'nav.products': 'Products & prices',
  'nav.purchases': 'Purchases',
  'nav.salesInvoices': 'Sales invoices',
  'nav.creditNotes': 'Credit notes',
  'nav.expenses': 'Expenses',
  'nav.loans': 'Loans & EMI',
  'nav.bankDeposits': 'Bank deposits',
  'nav.manage': 'Staff & data',
  'nav.businessCentral': 'Business Central',
  'nav.performance': 'My performance',
  'nav.targets': 'Targets',
  'nav.attendance': 'Daily attendance',
  'nav.allocations': 'Allocations',
  'nav.mySummary': 'Category & beat summary',
  'nav.collections': 'Collections',
  // Global approvals indicator.
  'approvals.waitingBanner': '{n} approval(s) waiting for you',
  'approvals.waitingHint': 'A field-staff request needs your decision.',
  'approvals.review': 'Review',
  // Sidebar "More" group headers (admin).
  'navgroup.sell': 'Sell',
  'navgroup.catalogue': 'Catalogue & stock',
  'navgroup.money': 'Money',
  'navgroup.team': 'Team',
  'navgroup.insights': 'Insights & sync',

  // Catalogue & cart
  'catalogue.title': 'Catalogue',
  'catalogue.addToCart': 'Add',
  'catalogue.inCart': 'In cart',
  'catalogue.inStock': 'In stock',
  'catalogue.low': 'Low stock',
  'catalogue.outOfStock': 'Out of stock',
  'catalogue.mrp': 'MRP',
  'catalogue.yourRate': 'Your rate',
  'catalogue.sameRateNote': 'The rate is the same for everyone. Your discount is applied on top.',
  'catalogue.repeatLast': 'Repeat last order',
  'catalogue.repeatLastHint': 'From {number} on {date}',
  'cart.title': 'Cart',
  'cart.empty': 'Your cart is empty',
  'cart.emptyHint': 'Add items from the catalogue',
  'cart.quantity': 'Qty',
  'cart.gross': 'Gross',
  'cart.discount': 'Discount',
  'cart.taxable': 'Taxable',
  'cart.gst': 'GST',
  'cart.cess': 'Cess',
  'cart.roundOff': 'Round off',
  'cart.payable': 'Payable',
  'cart.deliveryDate': 'Delivery date',
  'cart.deliverySlot': 'Preferred time',
  'cart.paymentMode': 'Payment',
  'cart.remarks': 'Note for us',
  'cart.placeOrder': 'Place order',
  'cart.placing': 'Placing order…',
  'cart.free': 'free',

  // Payment modes
  'pay.cod': 'Cash on delivery',
  'pay.upi_online': 'Pay by UPI',
  'pay.cash': 'Cash',
  'pay.full_payment': 'Pay in full',
  'pay.partial_payment': 'Part payment',
  'pay.credit': 'On account',
  // Payment at the door, recorded by the driver on the POD screen.
  'pay.atDoor': 'Payment',
  'pod.deliverNow': 'Deliver now',
  'pay.dueOnInvoice': 'Due on this invoice',
  'pay.nothing': 'Nothing collected',
  'pay.amountCollected': 'Amount collected',
  'pay.staysOnAccount': 'stays on the shop\u2019s account',
  'pay.nothingLeft': 'Nothing left on this invoice.',
  'pay.whyLabel': 'Why',
  'pay.whyHint': 'What the customer said',
  'pay.intoCashInHand': 'This goes into your cash in hand until you settle it at the office.',
  'pay.answerFirst': 'Say what happened about payment before saving.',
  'pay.enterAmount': 'Enter how much was collected.',
  'pay.moreThanDue': 'That is more than the customer owes on this invoice.',
  'pay.queuedOffline': 'Delivery saved. The payment will be sent when you have signal.',

  // Orders
  'order.title': 'Orders',
  'order.number': 'Order',
  'order.invoice': 'Invoice',
  'order.placed': 'Placed',
  'order.confirmed': 'Confirmed',
  'order.invoiced': 'Invoiced',
  'order.dispatched': 'Dispatched',
  'order.out_for_delivery': 'Pending Allocation for Action',
  'order.delivered': 'Delivered',
  'order.partially_delivered': 'Partly delivered',
  'order.closed': 'Closed',
  'order.cancelled': 'Cancelled',
  'order.printAllCopies': 'Print all 3 copies (office, driver, customer)',
  'order.viewInvoice': 'View invoice',
  'order.cancelOrder': 'Cancel order',
  'order.cancelReason': 'Why are you cancelling?',
  'order.cannotCancel': 'This order is out for delivery and can no longer be changed',
  'order.raiseReturn': 'Report a problem',
  'order.trackOrder': 'Track order',

  // Credit limit
  'credit.warningTitle': 'Over the credit limit',
  'credit.warningBody': 'This order takes the outstanding to {projected}, against a limit of {limit}.',
  'credit.override': 'Place it anyway',
  'credit.overrideNote': 'Admin will be notified',

  // Ledger & payments
  'ledger.title': 'Payments',
  'ledger.outstanding': 'Outstanding',
  'ledger.creditLimit': 'Credit limit',
  'ledger.available': 'Available',
  'ledger.ageing': 'Ageing',
  'ledger.underVerification': 'Under verification',
  'ledger.underVerificationHint': 'Not yet deducted from your outstanding',
  'ledger.payNow': 'Pay now',
  'ledger.scanQr': 'Scan to pay',
  'ledger.upiId': 'UPI ID',
  'ledger.uploadProof': 'Upload payment screenshot',
  'ledger.proofHint': 'We will verify and update your balance',
  'ledger.amountPaid': 'Amount paid',
  'ledger.utr': 'UPI reference (UTR)',
  'ledger.submitPayment': 'Submit for verification',
  'ledger.invoices': 'Invoices',
  'ledger.receipts': 'Receipts',
  'ledger.crates': 'Crates with you',
  'ledger.paid': 'Paid',
  'ledger.balance': 'Balance',
  'ledger.dueOn': 'Due {date}',
  'ledger.overdueBy': 'Overdue by {days} days',

  // Returns
  'return.title': 'Report a problem',
  'return.category': 'What is the problem?',
  'return.shortage': 'Short quantity',
  'return.damage': 'Damaged goods',
  'return.wrong_product': 'Wrong product',
  'return.leakage': 'Leaking bottles',
  'return.billing_mismatch': 'Billing mismatch',
  'return.quantity': 'How many?',
  'return.addPhoto': 'Add a photo',
  'return.remarks': 'Tell us more',
  'return.windowClosed': 'The window for this kind of return has closed',
  'return.windowHint': 'Must be reported within {hours} hours of delivery',
  'return.submitted': 'Reported. We will review it shortly.',
  'return.raised': 'Reported',
  'return.approved': 'Approved',
  'return.rejected': 'Not approved',
  'return.refunded': 'Refunded',

  // Delivery confirmation
  'delivery.confirmTitle': 'Confirm your delivery',
  'delivery.driverSaid': 'The driver recorded {n}',
  'delivery.howMany': 'How many did you receive?',
  'delivery.confirmReceipt': 'Confirm',
  'delivery.raiseDispute': 'Something is wrong',
  'delivery.autoConfirmIn': 'Confirms automatically in {hours} hours',
  'delivery.confirmed': 'Confirmed',
  'delivery.mismatchNote': 'We noticed a difference and our team will review it',

  // Salesperson — day
  'day.title': 'My day',
  'day.notStarted': 'Your day has not started',
  'day.startDay': 'Start day',
  'day.closeDay': 'Close day',
  'day.startedAt': 'Started {time}',
  'day.noTracking': 'No location is recorded',
  'day.outletsVisited': 'Outlets visited',
  'day.ordersBooked': 'Orders booked',
  'day.orderValue': 'Order value',
  'day.collections': 'Collections',
  'day.newCustomers': 'New customers',
  'day.noOrder': 'No order',
  'day.closeConfirm': 'Close your day? This creates the summary for admin.',
  'day.alreadyClosed': 'Your day is closed',

  // Beat
  'beat.title': 'Today’s beat',
  'beat.noBeat': 'No beat assigned today',
  'beat.outlets': '{n} outlets',
  'beat.visited': 'Visited',
  'beat.skipped': 'Skipped',
  'beat.pending': 'To visit',
  'beat.lastOrder': 'Last order {date}',
  'beat.neverOrdered': 'Never ordered',
  'beat.outstanding': 'Outstanding {amount}',
  'beat.visit': 'Visit',
  'beat.skip': 'Skip',
  'beat.skipReason': 'Why are you skipping?',

  // Visit
  'visit.title': 'Log visit',
  'visit.outcome': 'What happened?',
  'visit.order_booked': 'Order booked',
  'visit.no_order': 'No order',
  'visit.shop_closed': 'Customer closed',
  'visit.payment_promised': 'Payment promised',
  'visit.follow_up_needed': 'Follow up needed',
  'visit.collection_made': 'Collection made',
  'visit.competitor_push': 'Competitor pushing',
  'visit.other': 'Other',
  'visit.noOrderReason': 'Why no order?',
  'visit.sufficient_stock': 'Has enough stock',
  'visit.credit_issue': 'Credit issue',
  'visit.price_objection': 'Price objection',
  'visit.owner_absent': 'Owner not there',
  'visit.competitor_stock': 'Stocking competitor',
  'visit.seasonal_low': 'Slow season',
  'visit.notes': 'Discussion notes',
  'visit.followUpDate': 'Follow up on',
  'visit.competitor': 'Competitor seen',
  'visit.addPhotos': 'Add photos',
  'visit.photoTypes': 'Storefront, shelf, competitor',
  'visit.bookOrder': 'Book order',
  'visit.recordCollection': 'Record collection',
  'visit.markNoOrder': 'Mark no order',

  // Targets
  'target.title': 'Targets',
  'target.daily': 'Today',
  'target.monthly': 'This month',
  'target.cartons': 'Cartons',
  'target.value': 'Value',
  'target.customers': 'Customers',
  'target.achieved': 'Achieved',
  'target.noTarget': 'No target set',
  'target.incentive': 'Incentive',
  'target.incentiveNote': 'Calculated separately by admin',

  // Collections
  'collect.title': 'Record collection',
  'collect.amount': 'Amount collected',
  'collect.mode': 'How was it paid?',
  'collect.against': 'Against invoice',
  'collect.onAccount': 'On account',
  'collect.proof': 'Proof photo',
  'collect.cashInHand': 'Cash with you',
  'collect.cashInHandHint': 'Hand over to admin for settlement',
  'collect.handOver': 'Declare handover',
  'collect.declaredAmount': 'Amount you are handing over',
  'collect.awaitingConfirm': 'Awaiting admin confirmation',

  // Driver — routes
  'route.title': 'My routes',
  'route.noRoutes': 'No route assigned',
  'route.loadSheet': 'Load sheet',
  'route.stops': '{n} stops',
  'route.acceptLoad': 'Accept load',
  'route.acceptLoadHint': 'Check the quantities before accepting',
  'route.reportMismatch': 'Quantities do not match',
  'route.mismatchNote': 'What is different?',
  'route.startRoute': 'Start route',
  'route.accepted': 'Accepted',
  'route.inTransit': 'On route',
  'route.closeRoute': 'Close route',
  'route.loadIn': 'Return stock',
  'route.loadInHint': 'Count what is left on the vehicle',
  'route.counted': 'Counted',
  'route.expected': 'Expected',
  'route.variance': 'Difference',
  'route.varianceReason': 'Reason for the difference',
  'route.varianceRequired': 'A reason is needed, or this is flagged for admin',
  'route.vanStock': 'Stock on vehicle',
  'route.loaded': 'Loaded',
  'route.delivered': 'Delivered',
  'route.extraSold': 'Extra sold',
  'route.reconciled': 'Reconciled',
  'route.odometer': 'Odometer',

  // POD
  'pod.title': 'Proof of delivery',
  'pod.photoRequired': 'A delivery photo is required',
  'pod.photoHint': 'Photograph the goods at the customer',
  'pod.deliveredQty': 'Delivered',
  // The stepper on the POD screen counts this one; see the note in Pod.tsx.
  'pod.notDeliveredQty': 'Not delivered',
  'pod.shortageQty': 'Short',
  'pod.shortageReason': 'Reason for the shortage',
  'pod.crates': 'Crates',
  'pod.cratesIssued': 'Crates given',
  'pod.cratesCollected': 'Crates taken back',
  'pod.remarks': 'Notes',
  'pod.markDelivered': 'Mark delivered',
  'pod.markFailed': 'Could not deliver',
  'pod.failureReason': 'Why not?',
  'pod.shop_closed': 'Customer closed',
  'pod.customer_refused': 'Customer refused',
  'pod.payment_not_ready': 'Payment not ready',
  'pod.address_not_found': 'Address not found',
  'pod.vehicle_breakdown': 'Vehicle breakdown',
  'pod.awaitingAdmin': 'Admin will decide what to do next',
  'pod.extraBoxSale': 'Sell extra stock',
  'pod.extraBoxHint': 'Customer wants more than the invoice',

  // Admin
  'admin.dashboard': 'Dashboard',
  'admin.ordersToday': 'Orders today',
  'admin.collectionsToday': 'Collections today',
  'admin.outstanding': 'Outstanding',
  'admin.overdue': 'Overdue',
  'admin.cashPending': 'Cash pending settlement',
  'admin.cashPendingHint': 'Held by field staff',
  'admin.dispatches': 'Dispatches',
  'admin.stockAlerts': 'Stock alerts',
  'admin.approvalsPending': 'Approvals pending',
  'admin.syncStatus': 'Business Central sync',
  'admin.blockingStaff': '{n} blocking field staff',
  'admin.approve': 'Approve',
  'admin.reject': 'Reject',
  'admin.rejectReason': 'Reason for rejection',
  'admin.waitingFor': 'Waiting {minutes} min',
  'admin.noApprovals': 'Nothing waiting for you',
  'admin.setCreditLimit': 'Credit limit',
  'admin.setDiscount': 'Discount %',
  'admin.verifyPayment': 'Verify payment',
  'admin.verifyHint': 'Check the screenshot against your bank',
  'admin.correctedAmount': 'Actual amount received',
  'admin.settleCash': 'Confirm cash received',
  'admin.countedAmount': 'Amount you counted',
  'admin.cannotSettleOwn': 'You cannot confirm your own handover',

  // Chat
  'chat.title': 'Chat',
  'chat.noThreads': 'No conversations yet',
  'chat.typeMessage': 'Type a message',
  'chat.send': 'Send',
  'chat.internal': 'Internal',
  'chat.internalHint': 'Not visible to the customer',
  'chat.customerTab': 'With customer',
  'chat.imageOnly': 'Images only, up to 2 MB',
  'chat.deleted': 'Message deleted',
  'chat.deleteWindow': 'You can delete this for {minutes} more minutes',
  'chat.driverJoined': 'joined for this order',
  'chat.driverLeft': 'delivery completed',

  // Settings
  'settings.title': 'Settings',
  'settings.upi': 'UPI collection',
  'settings.upiId': 'UPI ID',
  'settings.upiHint': 'The QR shown to customers is generated from this',
  'settings.company': 'Company details',
  'settings.businessRules': 'Business rules',
  'settings.sync': 'Sync & storage',
  'settings.pendingItems': 'Waiting to sync',
  'settings.storageUsed': '{used} MB of {quota} MB used',
  'settings.syncNow': 'Sync now',
  'settings.installApp': 'Add to home screen',
  'settings.installHint': 'Works like an app, and works offline',
};

const hi: Dict = {
  'app.name': 'जीडी स्ट्रैटेजिक',
  'app.offline': 'ऑफ़लाइन',
  'app.offlineHint': 'आपका काम सेव है, कनेक्ट होने पर सिंक हो जाएगा',
  'app.syncing': 'सिंक हो रहा है…',
  'app.pendingSync': '{n} सिंक बाकी',
  'app.syncFailed': '{n} सिंक नहीं हुए',
  'app.retry': 'फिर कोशिश करें',
  'app.loading': 'लोड हो रहा है…',
  'app.somethingWrong': 'कुछ गड़बड़ हो गई',
  'app.tryAgain': 'फिर कोशिश करें',
  'app.noData': 'अभी कुछ नहीं',
  'app.staleData': 'सेव किया डेटा दिखा रहे हैं',
  'app.save': 'सेव करें',
  'app.edit': 'एडिट करें',
  'app.cancel': 'रद्द करें',
  'app.confirm': 'पुष्टि करें',
  'app.close': 'बंद करें',
  'app.back': 'पीछे',
  'app.next': 'आगे',
  'app.done': 'पूरा',
  'app.search': 'खोजें',
  'app.filter': 'फ़िल्टर',
  'app.clear': 'हटाएं',
  'app.total': 'कुल',
  'app.today': 'आज',
  'app.yesterday': 'कल',
  'app.optional': 'वैकल्पिक',
  'app.required': 'आवश्यक',
  'app.language': 'भाषा',
  'app.logout': 'साइन आउट',
  'app.account': 'आपका खाता',
  'app.settings': 'सेटिंग्स',
  'app.myDetails': 'मेरा विवरण',
  'app.logoutConfirm':
    'जो कुछ भेजा जाना बाकी है वह चला जाएगा। पहले सिंक पट्टी देखें।',
  'app.call': 'कॉल',
  'app.directions': 'रास्ता',
  'app.photo': 'फ़ोटो',
  'app.takePhoto': 'फ़ोटो लें',
  'app.retakePhoto': 'दोबारा लें',
  'app.remove': 'हटाएं',
  'app.submit': 'जमा करें',
  'app.submitting': 'जमा हो रहा है…',
  'app.all': 'सभी',
  'app.view': 'देखें',
  'app.viewPhoto': 'फ़ोटो देखें',
  'app.notesOptional': 'टिप्पणी (वैकल्पिक)',
  'app.reason': 'कारण',
  'app.from': 'से',
  'app.to': 'तक',
  'app.export': 'सीएसवी निर्यात',
  'app.refresh': 'रिफ़्रेश',
  'app.showMore': 'और दिखाएं',

  'auth.welcome': 'स्वागत है',
  'auth.welcomeLine': 'जीडी स्ट्रैटेजिक में आपका स्वागत है — जारी रखने के लिए साइन इन करें',
  'auth.otpNote': 'OTP पाने के लिए, व्हाट्सएप पर {number} पर "Hi" भेजें। '
                + 'कृपया इस नंबर को अपने फ़ोन में "GD Strategic Partner" के नाम से सेव करें।',
  'auth.enterMobile': 'अपना मोबाइल नंबर डालें',
  'auth.mobileHint': 'हम व्हाट्सएप पर कोड भेजेंगे',
  'auth.mobile': 'मोबाइल नंबर',
  'auth.sendCode': 'कोड भेजें',
  'auth.enterCode': '6 अंकों का कोड डालें',
  'auth.codeSentTo': '{phone} पर व्हाट्सएप से भेजा',
  'auth.verify': 'सत्यापित करें',
  'auth.resend': 'कोड फिर भेजें',
  'auth.resendIn': '{s} सेकंड में फिर भेजें',
  'auth.enterPin': 'अपना पिन डालें',
  'auth.pinHint': 'आपने जो 4 अंकों का पिन बनाया था',
  'auth.pin': 'पिन',
  'auth.signIn': 'साइन इन',
  'auth.setPin': 'पिन बनाएं',
  'auth.setPinHint': 'अगली बार कोड के बजाय यह इस्तेमाल होगा',
  'auth.confirmPin': 'पिन दोबारा डालें',
  'auth.pinMismatch': 'दोनों पिन मेल नहीं खाते',
  'auth.useCode': 'कोड से साइन इन करें',
  'auth.pendingApproval': 'आपका खाता मंज़ूरी की प्रतीक्षा में है',
  'auth.pendingApprovalHint': 'मंज़ूरी मिलने पर हम आपको बताएंगे',

  // The WhatsApp "hi" step
  'auth.hiTitle': 'पहले व्हाट्सएप पर "hi" भेजें',
  'auth.hiWhy': 'व्हाट्सएप हमें कोड भेजने की अनुमति तभी देता है जब आप हमें मैसेज कर दें। '
              + 'एक मैसेज भेजें और कोड तुरंत आ जाएगा।',
  'auth.hiWhyAgain': 'कोड फिर भेजने से पहले व्हाट्सएप को आपका नया मैसेज चाहिए।',
  'auth.hiRefused': 'व्हाट्सएप ने कोड नहीं भेजा — उसे अभी आपका मैसेज नहीं मिला है। '
                  + '"hi" भेजें और फिर कोशिश करें।',
  'auth.hiOpen': 'व्हाट्सएप खोलें और "hi" भेजें',
  'auth.hiAlreadySent': 'मैंने भेज दिया है — मेरा कोड भेजें',
  'auth.hiSendTo': '{number} पर भेजें',
  'auth.hiManual': 'अगर व्हाट्सएप नहीं खुला, तो {number} पर खुद मैसेज करें और फिर नीचे का बटन दबाएं।',
  'auth.hiChanged': 'दूसरा नंबर इस्तेमाल करें',
  'auth.staySignedIn': 'आप इस फ़ोन पर 6 महीने साइन इन रहेंगे।',

  'role.customer': 'ग्राहक',
  'role.salesperson': 'सेल्सपर्सन',
  'role.driver': 'ड्राइवर',
  'role.owner_admin': 'एडमिन',

  'nav.home': 'होम',
  'nav.catalogue': 'कैटलॉग',
  'nav.cart': 'कार्ट',
  'nav.orders': 'ऑर्डर',
  'nav.ledger': 'भुगतान',
  'nav.chat': 'चैट',
  'nav.myDetails': 'मेरा विवरण',
  'nav.mapView': 'मैप व्यू',
  'nav.day': 'मेरा दिन',
  'nav.beat': 'बीट',
  'nav.customers': 'ग्राहक',
  'nav.reminders': 'रिमाइंडर',
  'nav.requirements': 'आवश्यकताएँ',
  'nav.routes': 'रूट',
  'nav.dashboard': 'डैशबोर्ड',
  'nav.approvals': 'अनुमोदन',
  'nav.stock': 'स्टॉक',
  'nav.reports': 'रिपोर्ट',
  'nav.settings': 'सेटिंग्स',
  'nav.more': 'और',
  'nav.products': 'उत्पाद और दाम',
  'nav.purchases': 'खरीद',
  'nav.salesInvoices': 'बिक्री बिल',
  'nav.creditNotes': 'क्रेडिट नोट',
  'nav.expenses': 'खर्च',
  'nav.loans': 'ऋण और ई.एम.आई.',
  'nav.bankDeposits': 'बैंक जमा',
  'nav.manage': 'स्टाफ और डेटा',
  'nav.businessCentral': 'बिज़नेस सेंट्रल',
  'nav.performance': 'मेरा प्रदर्शन',
  'nav.targets': 'लक्ष्य',
  'nav.attendance': 'दैनिक उपस्थिति',
  'nav.allocations': 'आवंटन',
  'nav.mySummary': 'श्रेणी व बीट सारांश',
  'nav.collections': 'वसूली',
  'approvals.waitingBanner': '{n} अनुमोदन आपकी प्रतीक्षा में',
  'approvals.waitingHint': 'फ़ील्ड स्टाफ़ के अनुरोध पर आपका निर्णय चाहिए।',
  'approvals.review': 'देखें',
  'navgroup.sell': 'बिक्री',
  'navgroup.catalogue': 'कैटलॉग व स्टॉक',
  'navgroup.money': 'पैसा',
  'navgroup.team': 'टीम',
  'navgroup.insights': 'रिपोर्ट व सिंक',

  'catalogue.title': 'कैटलॉग',
  'catalogue.addToCart': 'जोड़ें',
  'catalogue.inCart': 'कार्ट में',
  'catalogue.inStock': 'स्टॉक में',
  'catalogue.low': 'कम स्टॉक',
  'catalogue.outOfStock': 'स्टॉक खत्म',
  'catalogue.mrp': 'एमआरपी',
  'catalogue.yourRate': 'आपका रेट',
  'catalogue.sameRateNote': 'रेट सभी के लिए एक है। आपकी छूट अलग से लगती है।',
  'catalogue.repeatLast': 'पिछला ऑर्डर दोहराएं',
  'catalogue.repeatLastHint': '{date} का {number}',
  'cart.title': 'कार्ट',
  'cart.empty': 'आपका कार्ट खाली है',
  'cart.emptyHint': 'कैटलॉग से आइटम जोड़ें',
  'cart.quantity': 'मात्रा',
  'cart.gross': 'सकल',
  'cart.discount': 'छूट',
  'cart.taxable': 'कर योग्य',
  'cart.gst': 'जीएसटी',
  'cart.cess': 'सेस',
  'cart.roundOff': 'राउंड ऑफ',
  'cart.payable': 'देय',
  'cart.deliveryDate': 'डिलीवरी तिथि',
  'cart.deliverySlot': 'पसंदीदा समय',
  'cart.paymentMode': 'भुगतान',
  'cart.remarks': 'हमारे लिए नोट',
  'cart.placeOrder': 'ऑर्डर दें',
  'cart.placing': 'ऑर्डर हो रहा है…',
  'cart.free': 'मुफ़्त',

  'pay.cod': 'डिलीवरी पर नकद',
  'pay.upi_online': 'यूपीआई से भुगतान',
  'pay.cash': 'नकद',
  'pay.full_payment': 'पूरा भुगतान',
  'pay.partial_payment': 'आंशिक भुगतान',
  'pay.credit': 'खाते पर',
  'pay.atDoor': 'भुगतान',
  'pod.deliverNow': 'अभी डिलीवर करें',
  'pay.dueOnInvoice': 'इस बिल पर बाकी',
  'pay.nothing': 'कुछ नहीं लिया',
  'pay.amountCollected': 'ली गई रकम',
  'pay.staysOnAccount': 'दुकान के खाते पर बाकी रहेगा',
  'pay.nothingLeft': 'इस बिल पर कुछ बाकी नहीं।',
  'pay.whyLabel': 'कारण',
  'pay.whyHint': 'दुकानदार ने क्या कहा',
  'pay.intoCashInHand': 'यह रकम आपके पास नकद में रहेगी, ऑफिस में जमा करने तक।',
  'pay.answerFirst': 'सेव करने से पहले भुगतान के बारे में बताएं।',
  'pay.enterAmount': 'कितनी रकम ली, वह भरें।',
  'pay.moreThanDue': 'यह रकम दुकान के बाकी से ज्यादा है।',
  'pay.queuedOffline': 'डिलीवरी सेव हो गई। नेटवर्क आने पर भुगतान भेजा जाएगा।',

  'order.title': 'ऑर्डर',
  'order.number': 'ऑर्डर',
  'order.invoice': 'इनवॉइस',
  'order.placed': 'दिया गया',
  'order.confirmed': 'पुष्ट',
  'order.invoiced': 'बिल बना',
  'order.dispatched': 'भेजा गया',
  'order.out_for_delivery': 'कार्रवाई हेतु आवंटन लंबित',
  'order.delivered': 'डिलीवर हुआ',
  'order.partially_delivered': 'आंशिक डिलीवर',
  'order.closed': 'बंद',
  'order.cancelled': 'रद्द',
  'order.printAllCopies': 'तीनों कॉपी प्रिंट करें (ऑफिस, ड्राइवर, ग्राहक)',
  'order.viewInvoice': 'इनवॉइस देखें',
  'order.cancelOrder': 'ऑर्डर रद्द करें',
  'order.cancelReason': 'रद्द करने का कारण?',
  'order.cannotCancel': 'यह ऑर्डर डिलीवरी पर है, अब बदला नहीं जा सकता',
  'order.raiseReturn': 'समस्या बताएं',
  'order.trackOrder': 'ऑर्डर ट्रैक करें',

  'credit.warningTitle': 'क्रेडिट सीमा से अधिक',
  'credit.warningBody': 'इस ऑर्डर से बकाया {projected} हो जाएगा, सीमा {limit} है।',
  'credit.override': 'फिर भी ऑर्डर करें',
  'credit.overrideNote': 'एडमिन को सूचित किया जाएगा',

  'ledger.title': 'भुगतान',
  'ledger.outstanding': 'बकाया',
  'ledger.creditLimit': 'क्रेडिट सीमा',
  'ledger.available': 'उपलब्ध',
  'ledger.ageing': 'बकाया अवधि',
  'ledger.underVerification': 'सत्यापन में',
  'ledger.underVerificationHint': 'अभी बकाया से घटाया नहीं गया',
  'ledger.payNow': 'अभी भुगतान करें',
  'ledger.scanQr': 'स्कैन करके भुगतान करें',
  'ledger.upiId': 'यूपीआई आईडी',
  'ledger.uploadProof': 'भुगतान का स्क्रीनशॉट अपलोड करें',
  'ledger.proofHint': 'हम सत्यापित करके बैलेंस अपडेट करेंगे',
  'ledger.amountPaid': 'भुगतान राशि',
  'ledger.utr': 'यूपीआई रेफरेंस (UTR)',
  'ledger.submitPayment': 'सत्यापन के लिए भेजें',
  'ledger.invoices': 'इनवॉइस',
  'ledger.receipts': 'रसीदें',
  'ledger.crates': 'आपके पास क्रेट',
  'ledger.paid': 'भुगतान',
  'ledger.balance': 'शेष',
  'ledger.dueOn': 'देय {date}',
  'ledger.overdueBy': '{days} दिन से अतिदेय',

  'return.title': 'समस्या बताएं',
  'return.category': 'क्या समस्या है?',
  'return.shortage': 'मात्रा कम',
  'return.damage': 'खराब माल',
  'return.wrong_product': 'गलत उत्पाद',
  'return.leakage': 'बोतल रिस रही',
  'return.billing_mismatch': 'बिलिंग में अंतर',
  'return.quantity': 'कितने?',
  'return.addPhoto': 'फ़ोटो जोड़ें',
  'return.remarks': 'विस्तार से बताएं',
  'return.windowClosed': 'इस तरह की वापसी की अवधि समाप्त',
  'return.windowHint': 'डिलीवरी के {hours} घंटे में बताना होगा',
  'return.submitted': 'दर्ज हो गया। हम शीघ्र देखेंगे।',
  'return.raised': 'दर्ज',
  'return.approved': 'स्वीकृत',
  'return.rejected': 'अस्वीकृत',
  'return.refunded': 'रिफंड हुआ',

  'delivery.confirmTitle': 'अपनी डिलीवरी की पुष्टि करें',
  'delivery.driverSaid': 'ड्राइवर ने {n} दर्ज किया',
  'delivery.howMany': 'आपको कितने मिले?',
  'delivery.confirmReceipt': 'पुष्टि करें',
  'delivery.raiseDispute': 'कुछ गलत है',
  'delivery.autoConfirmIn': '{hours} घंटे में स्वतः पुष्टि',
  'delivery.confirmed': 'पुष्ट',
  'delivery.mismatchNote': 'अंतर मिला है, हमारी टीम देखेगी',

  'day.title': 'मेरा दिन',
  'day.notStarted': 'आपका दिन शुरू नहीं हुआ',
  'day.startDay': 'दिन शुरू करें',
  'day.closeDay': 'दिन बंद करें',
  'day.startedAt': '{time} पर शुरू',
  'day.noTracking': 'कोई लोकेशन दर्ज नहीं होती',
  'day.outletsVisited': 'दुकानें देखीं',
  'day.ordersBooked': 'ऑर्डर लिए',
  'day.orderValue': 'ऑर्डर मूल्य',
  'day.collections': 'वसूली',
  'day.newCustomers': 'नए ग्राहक',
  'day.noOrder': 'ऑर्डर नहीं',
  'day.closeConfirm': 'दिन बंद करें? एडमिन के लिए सारांश बनेगा।',
  'day.alreadyClosed': 'आपका दिन बंद है',

  'beat.title': 'आज की बीट',
  'beat.noBeat': 'आज कोई बीट नहीं',
  'beat.outlets': '{n} दुकानें',
  'beat.visited': 'देखी',
  'beat.skipped': 'छोड़ी',
  'beat.pending': 'बाकी',
  'beat.lastOrder': 'पिछला ऑर्डर {date}',
  'beat.neverOrdered': 'कभी ऑर्डर नहीं',
  'beat.outstanding': 'बकाया {amount}',
  'beat.visit': 'विज़िट',
  'beat.skip': 'छोड़ें',
  'beat.skipReason': 'छोड़ने का कारण?',

  'visit.title': 'विज़िट दर्ज करें',
  'visit.outcome': 'क्या हुआ?',
  'visit.order_booked': 'ऑर्डर मिला',
  'visit.no_order': 'ऑर्डर नहीं',
  'visit.shop_closed': 'दुकान बंद',
  'visit.payment_promised': 'भुगतान का वादा',
  'visit.follow_up_needed': 'फिर आना है',
  'visit.collection_made': 'वसूली हुई',
  'visit.competitor_push': 'प्रतिस्पर्धी दबाव',
  'visit.other': 'अन्य',
  'visit.noOrderReason': 'ऑर्डर क्यों नहीं?',
  'visit.sufficient_stock': 'स्टॉक पर्याप्त',
  'visit.credit_issue': 'क्रेडिट समस्या',
  'visit.price_objection': 'कीमत पर आपत्ति',
  'visit.owner_absent': 'मालिक नहीं थे',
  'visit.competitor_stock': 'प्रतिस्पर्धी माल',
  'visit.seasonal_low': 'सीज़न मंदा',
  'visit.notes': 'बातचीत के नोट्स',
  'visit.followUpDate': 'अगली बार',
  'visit.competitor': 'प्रतिस्पर्धी दिखा',
  'visit.addPhotos': 'फ़ोटो जोड़ें',
  'visit.photoTypes': 'दुकान, शेल्फ, प्रतिस्पर्धी',
  'visit.bookOrder': 'ऑर्डर लें',
  'visit.recordCollection': 'वसूली दर्ज करें',
  'visit.markNoOrder': 'ऑर्डर नहीं दर्ज करें',

  'target.title': 'लक्ष्य',
  'target.daily': 'आज',
  'target.monthly': 'इस महीने',
  'target.cartons': 'कार्टन',
  'target.value': 'मूल्य',
  'target.customers': 'ग्राहक',
  'target.achieved': 'हासिल',
  'target.noTarget': 'कोई लक्ष्य नहीं',
  'target.incentive': 'प्रोत्साहन',
  'target.incentiveNote': 'एडमिन द्वारा अलग से गणना',

  'collect.title': 'वसूली दर्ज करें',
  'collect.amount': 'वसूली राशि',
  'collect.mode': 'भुगतान कैसे हुआ?',
  'collect.against': 'इनवॉइस के विरुद्ध',
  'collect.onAccount': 'खाते पर',
  'collect.proof': 'सबूत फ़ोटो',
  'collect.cashInHand': 'आपके पास नकद',
  'collect.cashInHandHint': 'निपटान के लिए एडमिन को दें',
  'collect.handOver': 'हस्तांतरण घोषित करें',
  'collect.declaredAmount': 'आप कितना दे रहे हैं',
  'collect.awaitingConfirm': 'एडमिन पुष्टि की प्रतीक्षा',

  'route.title': 'मेरे रूट',
  'route.noRoutes': 'कोई रूट नहीं',
  'route.loadSheet': 'लोड शीट',
  'route.stops': '{n} स्टॉप',
  'route.acceptLoad': 'लोड स्वीकारें',
  'route.acceptLoadHint': 'स्वीकारने से पहले मात्रा जांचें',
  'route.reportMismatch': 'मात्रा मेल नहीं खाती',
  'route.mismatchNote': 'क्या अंतर है?',
  'route.startRoute': 'रूट शुरू करें',
  'route.accepted': 'स्वीकृत',
  'route.inTransit': 'रूट पर',
  'route.closeRoute': 'रूट बंद करें',
  'route.loadIn': 'स्टॉक वापस',
  'route.loadInHint': 'गाड़ी में बचा माल गिनें',
  'route.counted': 'गिना',
  'route.expected': 'अपेक्षित',
  'route.variance': 'अंतर',
  'route.varianceReason': 'अंतर का कारण',
  'route.varianceRequired': 'कारण देना ज़रूरी है, वरना एडमिन को फ्लैग होगा',
  'route.vanStock': 'गाड़ी में स्टॉक',
  'route.loaded': 'लोड',
  'route.delivered': 'डिलीवर',
  'route.extraSold': 'अतिरिक्त बिका',
  'route.reconciled': 'मिलान हुआ',
  'route.odometer': 'ओडोमीटर',

  'pod.title': 'डिलीवरी प्रमाण',
  'pod.photoRequired': 'डिलीवरी फ़ोटो आवश्यक है',
  'pod.photoHint': 'दुकान पर माल की फ़ोटो लें',
  'pod.deliveredQty': 'डिलीवर',
  'pod.notDeliveredQty': 'नहीं दिया',
  'pod.shortageQty': 'कम',
  'pod.shortageReason': 'कमी का कारण',
  'pod.crates': 'क्रेट',
  'pod.cratesIssued': 'क्रेट दिए',
  'pod.cratesCollected': 'क्रेट वापस लिए',
  'pod.remarks': 'नोट्स',
  'pod.markDelivered': 'डिलीवर दर्ज करें',
  'pod.markFailed': 'डिलीवर नहीं हुआ',
  'pod.failureReason': 'क्यों नहीं?',
  'pod.shop_closed': 'दुकान बंद',
  'pod.customer_refused': 'ग्राहक ने मना किया',
  'pod.payment_not_ready': 'भुगतान तैयार नहीं',
  'pod.address_not_found': 'पता नहीं मिला',
  'pod.vehicle_breakdown': 'गाड़ी खराब',
  'pod.awaitingAdmin': 'एडमिन तय करेंगे आगे क्या करना है',
  'pod.extraBoxSale': 'अतिरिक्त स्टॉक बेचें',
  'pod.extraBoxHint': 'दुकान को इनवॉइस से ज्यादा चाहिए',

  'admin.dashboard': 'डैशबोर्ड',
  'admin.ordersToday': 'आज के ऑर्डर',
  'admin.collectionsToday': 'आज की वसूली',
  'admin.outstanding': 'बकाया',
  'admin.overdue': 'अतिदेय',
  'admin.cashPending': 'निपटान बाकी नकद',
  'admin.cashPendingHint': 'फील्ड स्टाफ के पास',
  'admin.dispatches': 'डिस्पैच',
  'admin.stockAlerts': 'स्टॉक चेतावनी',
  'admin.approvalsPending': 'अनुमोदन लंबित',
  'admin.syncStatus': 'बिज़नेस सेंट्रल सिंक',
  'admin.blockingStaff': '{n} फील्ड स्टाफ रुका है',
  'admin.approve': 'स्वीकृत करें',
  'admin.reject': 'अस्वीकार करें',
  'admin.rejectReason': 'अस्वीकार का कारण',
  'admin.waitingFor': '{minutes} मिनट प्रतीक्षा',
  'admin.noApprovals': 'आपके लिए कुछ लंबित नहीं',
  'admin.setCreditLimit': 'क्रेडिट सीमा',
  'admin.setDiscount': 'छूट %',
  'admin.verifyPayment': 'भुगतान सत्यापित करें',
  'admin.verifyHint': 'स्क्रीनशॉट को बैंक से मिलाएं',
  'admin.correctedAmount': 'वास्तविक प्राप्त राशि',
  'admin.settleCash': 'नकद प्राप्ति की पुष्टि',
  'admin.countedAmount': 'आपने कितना गिना',
  'admin.cannotSettleOwn': 'आप अपना हस्तांतरण पुष्ट नहीं कर सकते',

  'chat.title': 'चैट',
  'chat.noThreads': 'अभी कोई बातचीत नहीं',
  'chat.typeMessage': 'संदेश लिखें',
  'chat.send': 'भेजें',
  'chat.internal': 'आंतरिक',
  'chat.internalHint': 'ग्राहक को नहीं दिखता',
  'chat.customerTab': 'ग्राहक के साथ',
  'chat.imageOnly': 'केवल छवियां, 2 एमबी तक',
  'chat.deleted': 'संदेश हटाया गया',
  'chat.deleteWindow': 'आप इसे {minutes} मिनट और हटा सकते हैं',
  'chat.driverJoined': 'इस ऑर्डर के लिए जुड़े',
  'chat.driverLeft': 'डिलीवरी पूरी हुई',

  'settings.title': 'सेटिंग्स',
  'settings.upi': 'यूपीआई वसूली',
  'settings.upiId': 'यूपीआई आईडी',
  'settings.upiHint': 'ग्राहकों को दिखने वाला क्यूआर इससे बनता है',
  'settings.company': 'कंपनी विवरण',
  'settings.businessRules': 'व्यावसायिक नियम',
  'settings.sync': 'सिंक और स्टोरेज',
  'settings.pendingItems': 'सिंक बाकी',
  'settings.storageUsed': '{quota} एमबी में {used} एमबी उपयोग',
  'settings.syncNow': 'अभी सिंक करें',
  'settings.installApp': 'होम स्क्रीन पर जोड़ें',
  'settings.installHint': 'ऐप की तरह चलता है, ऑफ़लाइन भी',
};

const dictionaries: Record<Locale, Dict> = { en, hi };

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  /** Translate, with {placeholder} substitution. */
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue>({
  locale: 'en',
  setLocale: () => {},
  t: (k) => k,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');

  useEffect(() => {
    void (async () => {
      const stored = await idb.kvGet<Locale>('locale');
      if (stored === 'en' || stored === 'hi') { setLocaleState(stored); return; }
      // Fall back to the device language — a Hindi-locale phone should not have
      // to hunt for the switch.
      const nav = navigator.language?.toLowerCase() ?? 'en';
      setLocaleState(nav.startsWith('hi') ? 'hi' : 'en');
    })();
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    void idb.kvSet('locale', l);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      // Fall back to English, then to the key itself. A missing Hindi string
      // shows readable English rather than a raw dotted key.
      const template = dictionaries[locale][key] ?? en[key] ?? key;
      if (!vars) return template;
      return template.replace(/\{(\w+)\}/g, (_, name: string) =>
        String(vars[name] ?? `{${name}}`),
      );
    },
    [locale],
  );

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}

/** Convenience: just the translate function. */
export function useT(): I18nValue['t'] {
  return useContext(I18nContext).t;
}
